import { get, post, put, remove } from '../Services/http';
import { catchError } from './Error.action';
import {
  OFFER_LOAD_ERROR,
  OFFERS_RECEIVE,
  OFFERS_SHOW_OFFER_MENU,
  OFFERS_SET_ACTIVE,
  OFFERS_ON_OFFER_CLICKED,
} from '../constants';
import { addNotification } from '../Services/Notification.service';
import { translate } from '../Services/translation';
import { receive as receiveCart } from './Cart.action';

export const loadOffers = () => (dispatch) => {
  return get('/api/offers')
    .then((response) => response.json())
    .then((offers) => dispatch(receiveOffers(offers)))
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const createNewOffer = ({ name, note }) => (dispatch, getState) => {
  const { offers } = getState().offers;

  return post('/api/offers', { name, note })
    .then((res) => res.json())
    .then((offer) => {
      dispatch(receiveOffers([offer, ...offers]));
      dispatch(setActiveOffer(offer.id));
      addNotification({
        type: 'success',
        text: translate('offer-modal.offer-created'),
      });
    })
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const deleteOffer = (offerSystemId) => (dispatch, getState) => {
  const { offers } = getState().offers;
  const offerIndex = offers.findIndex((o) => o.id == offerSystemId);

  return remove(`/api/offers/${offerSystemId}`)
    .then(() => {
      if (offerIndex != -1) {
        offers.splice(offerIndex, 1);
      }
      dispatch(receiveOffers(offers));
      dispatch(setActiveOffer(null));
      addNotification({
        type: 'success',
        text: translate('offer-modal.offer-deleted'),
      });
    })
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const getOfferSessionKey = (offerSystemId) => {
  const url = `/api/offers/${offerSystemId}/save`;
  return post(url).then((res) => res.json());
};

export const setActiveOffer = (activeOfferSystemId) => (dispatch) => {
  let url = '/api/offers/active';
  if (!!activeOfferSystemId) {
    url += `?offerSystemId=${activeOfferSystemId}`;
  }

  return post(url)
    .then(() => {
      dispatch({
        type: OFFERS_SET_ACTIVE,
        payload: {
          activeOfferSystemId,
        },
      });
    })
    .catch((ex) => dispatch(catchError(ex, setOfferModalError)));
};

export const setOfferModalError = (error) => ({
  type: OFFER_LOAD_ERROR,
  payload: {
    error,
  },
});

export const receiveOffers = (offers) => ({
  type: OFFERS_RECEIVE,
  payload: {
    offers,
  },
});

export const receiveSingleOffer = (offer) => (dispatch, getState) => {
  const { offers } = getState().offers;
  const offerIndex = offers.findIndex((o) => o.id == offer.id);
  if (offerIndex != -1) {
    offers.splice(offerIndex, 1, offer);
  } else {
    offers.push(offer);
  }

  dispatch({
    type: OFFERS_RECEIVE,
    payload: {
      offers,
    },
  });
};

export const toggleOfferMenu = (
  showOfferMenu = null,
  onOfferClicked = null
) => (dispatch, getState) => {
  if (showOfferMenu != null) {
    dispatch(show(showOfferMenu));
  } else {
    dispatch(show(!getState().offers.showOfferMenu));
  }
  dispatch(setOnOfferClicked(onOfferClicked));
};

const show = (visible) => ({
  type: OFFERS_SHOW_OFFER_MENU,
  payload: {
    showOfferMenu: visible,
  },
});

const setOnOfferClicked = (onOfferClicked) => ({
  type: OFFERS_ON_OFFER_CLICKED,
  payload: {
    onOfferClicked,
  },
});

export const addItemToOffer = (offerSystemId, item, abortController = null) => (
  dispatch
) => {
  return post(`/api/offers/${offerSystemId}/items`, item, abortController)
    .then((response) => response.json())
    .then((offer) => {
      dispatch(receiveSingleOffer(offer));
      addNotification({
        type: 'success',
        text: translate('offer-modal.item-added-to-offer'),
      });
    })
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const updateItemInOffer = (
  offerSystemId,
  itemSystemId,
  item,
  abortController = null
) => (dispatch) => {
  return post(
    `/api/offers/${offerSystemId}/items/${itemSystemId}`,
    item,
    abortController
  )
    .then((response) => response.json())
    .then((offer) => {
      dispatch(receiveSingleOffer(offer));
    })
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const clear = (abortController = null) => (dispatch) => {
  return post(`/api/offers/clear`, null, abortController)
    .then((response) => response.json())
    .then((offer) => dispatch(receiveSingleOffer(offer)))
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const setRowNote = (rowSystemId, note, abortController = null) => (
  dispatch
) => {
  return put(`/api/offers/rows/${rowSystemId}/note`, { note }, abortController)
    .then((response) => response.json())
    .then((offer) => dispatch(receiveSingleOffer(offer)))
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const setOfferNote = (offerSystemId, note, abortController = null) => (
  dispatch
) => {
  return put(`/api/offers/${offerSystemId}/note`, { note }, abortController)
    .then((response) => response.json())
    .then((offer) => dispatch(receiveSingleOffer(offer)))
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const sendOfferToCart = (offerSystemId, abortController = null) => (
  dispatch
) => {
  return post(`/api/offers/${offerSystemId}/sendtocart`, abortController)
    .then((response) => response.json())
    .then((cart) => dispatch(receiveCart(cart)))
    .then(() => {
      dispatch(toggleOfferMenu(false));
      addNotification({
        type: 'success',
        text: translate('offer-modal.sent-to-cart'),
      });
    })
    .catch((ex) =>
      dispatch(catchError(ex, (error) => setOfferModalError(error)))
    );
};

export const sendOfferAsEmail = (
  offerSystemId,
  toEmail,
  abortController = null
) => {
  return post(
    `/api/offers/${offerSystemId}/sendmail?toEmail=${toEmail}`,
    abortController
  );
};
